import React, { useEffect, useState } from "react";
import { Constants } from "@eagerdog/constants";
import moment from "moment-timezone";
import { CourseDto, ICourseSummary, IContact, ISession, IDog } from "@eagerdog/interfaces";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { AxiosError } from "axios";

import { helperService } from "src/services/helper.service";
import { apiService } from "src/services";
import { localStorageService } from "src/services/local-storage.service";

import { Modal, useModal } from "src/components/Modal/Modal";
import Breadcrumb, { ICrumb } from "src/components/Breadcrumb/Breadcrumb";
import TabContent, { ITab, useTabContent } from "src/components/TabContent/TabContent";
import CreateCourse, { COURSE_VISIBILITY_STEP } from "src/routes/Courses/CreateCourse/CreateCourse";
import PublishCourseModal from "src/components/PublishCourseModal/PublishCourseModal";
import Button from "src/components/Button/Button";
import DogPortrait from "src/components/DogPortrait/DogPortrait";
import Confirm from "src/components/Confirm/Confirm";
import UserSearch from "src/components/UserSearch/UserSearch";
import { toast } from "src/components/Toast/ToastManager";

import CourseSchedule from "./CourseSchedule/CourseSchedule";
import AddSessionModal from "./CourseSchedule/AddSessionModal";
import CourseDocumentsTab from "./DocumentsTab/DocumentsTab";

import styles from "./Course.module.scss";

interface IProps {
    cid?: string;
}

const Course: React.FC<IProps> = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cid } = useParams<{ cid: string }>();
  const user = helperService.getUser();
  const { isShown, toggle } = useModal();

  const [loading, setLoading] = useState<boolean>(true);
  const [refreshCourse, setRefreshCourse] = useState<boolean>(false);
  const [showAddSession, setShowAddSession] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<string | null>(null);
  const [courseFormInitialTab, setCourseFormInitialTab] = useState<number | undefined>(undefined);
  const { activeTab, setTab } = useTabContent();

  const [crumbs, setCrumbs] = useState<ICrumb[]>([
      { label: "Courses", path: "/courses" },
      { label: "..." }
  ]);
  
  // Track where we came from using location state
  const cameFrom = location.state?.from || 'courses';

  const [tabs] = useState<ITab[]>([
    { name: "Schedule", actions: [] },
    { name: "Documents", actions: [] }
  ]);

  const [course, setCourse] = useState<ICourseSummary | null>(null);
  const [registeredDogs, setRegisteredDogs] = useState<IDog[]>([]);
  const [instructors, setInstructors] = useState<IContact[]>([]);
  const [isAdminUser, setIsAdminUser] = useState<boolean>(false);

  const isAdmin = () => {
    if (course) {
      // Course creator is always admin
      if (course.created_by === user._id) {
        return true;
      }
      // Club managers are admins only for clubs where they have admin capacity
      if (user.user_type === Constants.user_type.club_manager) {
        const capacities = helperService.getCapacities();
        return capacities.some(
          (cap: any) =>
            cap.club_id?.toString() === course.club_id?.toString() &&
            cap.capacities?.includes(Constants.capacity_type.admin)
        );
      }
      // Dog owners with admin capacity for this club
      if (isAdminUser) {
        return true;
      }
    }

    return false;
  }

  const archiveCourse = () => {
    apiService.updateCourse(course!._id, { ...course as CourseDto, status: Constants.course_status.archived }).then((response) => {
        toast.show({
          title: "Course",
          content: "Course successfully deleted.",
          duration: 5000,
          type: "fail"
        });

        navigate("/courses");
    });
  }

  const getModalContent = () => {
    switch (modalContent) {
      case "editCourse":
        return course ? <CreateCourse
          initialTab={courseFormInitialTab}
          onSubmit={() => {
            toggle();
            setModalContent(null);
            setCourseFormInitialTab(undefined);
            setLoading(true);
          }}
          course={course} /> : <></>;
      case "archiveCourse":
        return course ? <Confirm
          message={"Are you sure you want to delete this course?"}
          onConfirm={() => {
            archiveCourse();
            navigate("/courses");
            setModalContent(null);
            toggle();
          }}
          onCancel={() => {

          }}
        /> : <></>;
      case "searchUsersModal":
        return course ? <UserSearch
          course={course.handle}
        /> : <></>;
      case "publishCourse":
        return course ? <PublishCourseModal
          course={course}
          toggle={() => {
            toggle();
            setModalContent(null);
          }}
          afterUpdate={() => {
            setLoading(true);
          }}
        /> : <></>;
    }

    return <></>;
  }
  
  const getSessionCount = () => {
    if (!course?.schedule?.length) return 0;
    // For full courses, all sessions are booked as a block — use the first session's entries
    if (course.course_type === Constants.course_type.full_course) {
      return course.schedule[0].regular_entries || 0;
    }
    // For drop-in/seminar, sum across all sessions
    return course.schedule.reduce((total, session) => total + (session.regular_entries || 0), 0);
  }

  const getMaxDogCount = () => {
    if (!course?.schedule?.length) return 0;
    // For full courses, capacity is per-session (all sessions share the same limit)
    if (course.course_type === Constants.course_type.full_course) {
      return course.schedule[0].regular_capacity || 0;
    }
    // For drop-in/seminar, sum across all sessions
    return course.schedule.reduce((total, session) => total + (session.regular_capacity || 0), 0);
  }

  const isSeminar = course?.course_type === Constants.course_type.seminar;
  const isClass = course?.course_type === Constants.course_type.drop_in;
  const entryLimit = course?.has_entry_limit ? (course.entry_limit || 0) : (isClass ? getMaxDogCount() : getMaxDogCount());
  const enrolledCount = course?.enrolled_count ?? getSessionCount();
  const waitlistCount = course?.waitlist_count || 0;
  const waitlistSize = course?.waitlist_size || 0;
  const hasRoom = !course?.has_entry_limit || enrolledCount < entryLimit;
  const waitlistOpen = !hasRoom && waitlistSize > 0 && waitlistCount < waitlistSize;
  const isClosed = course?.has_entry_limit && !hasRoom && !waitlistOpen;

  const isCourseFull = () => {
    if (isClass) return false;
    return !!isClosed;
  }

  const getCourseFee = () => {
    if (course) {
      if (course.schedule && course.schedule.length > 0 && course.schedule[0].fee !== undefined) {
        // For classes (drop-in) and seminars, show per-session pricing
        if (course.course_type === Constants.course_type.drop_in || course.course_type === Constants.course_type.seminar) {
          // Get all unique fees
          const fees = course.schedule
            .map(session => session.fee)
            .filter((fee): fee is number => fee !== undefined);
          
          if (fees.length === 0) {
            return course.fee;
          }
          
          const minFee = Math.min(...fees);
          const maxFee = Math.max(...fees);
          
          // If all fees are the same, show single price
          if (minFee === maxFee) {
            return minFee;
          }
          
          // If fees vary, show range
          return `${minFee} - ${maxFee}`;
        }
        
        // For full courses, show sum of all session fees
        let total: number = 0;
        course.schedule.forEach((session: ISession) => {
          if (session.fee !== undefined) {
            total += session.fee;
          }
        });
        return total;
      }
    }
  
    return course!.fee;
  }

  // Helper to determine pricing label
  const getPricingLabel = () => {
    if (!course) return "";
    
    // Check if course has per-session pricing
    const hasSessionFees = course.schedule && course.schedule.length > 0 && course.schedule[0].fee !== undefined;
    
    if (hasSessionFees) {
      // Classes and seminars with session fees
      if (course.course_type === Constants.course_type.drop_in || course.course_type === Constants.course_type.seminar) {
        return "per session";
      }
      // Full courses with session fees (shouldn't happen but handle it)
      return "per course";
    }
    
    // No session fees means total course pricing
    return "per course";
  };

  const isCourseLive = () => {
    if (!course) return false;
    if (course.status === Constants.course_status.published) {
      return true;
    }
    if (course.status === Constants.course_status.upcoming) {
      if (!course.publish_at) {
        return true;
      }
      return new Date(course.publish_at) <= new Date();
    }
    return false;
  };

  const publishTimeLabel = () => {
    if (!course?.publish_at) {
      return "";
    }
    const formatted = moment(course.publish_at).tz(course.timezone || moment.tz.guess()).format("MMMM Do, YYYY @ h:mma zz");
    if (course.status === Constants.course_status.draft || course.status === Constants.course_status.upcoming) {
      return `Set to go live on ${formatted}`;
    }
    if (course.status === Constants.course_status.published) {
      return `Live on ${formatted}`;
    }
    return "";
  };

  useEffect(() => {
    if (loading || refreshCourse) {
        // Only scroll to top on initial load, not on refresh
        if (!course) {
          window.scrollTo(0, 0);
        }
        
        const _cid = props.cid || cid;

        if (!_cid) {
            return;
        }

      apiService.getCourseSummaryForHandle(_cid).then(async (course) => {
        if (course.status === Constants.course_status.archived) {
          navigate("/courses");

          toast.show({
            title: "Course Error",
            content: "Course has been deleted",
            duration: 5000,
            type: "fail"
          });
        }

        if (cameFrom === 'club' && course.club_id) {
          setCrumbs([
            { label: course.club_name || course.club?.club_name || "Club", path: `/k/${course.club_id}` },
            { label: course.title }
          ]);
        } else {
          setCrumbs([
            { label: "Courses", path: "/courses" },
            { label: course.title }
          ]);
        }
        

        setCourse(course);
        localStorageService.addRecentActivity({
          type: "course",
          id: course._id,
          name: course.title,
          link: `/c/${course.handle}`,
          logo_url: course.club?.logo_url,
        });

        // Check if user has admin capacity for this club
        if (user.user_type === Constants.user_type.owner) {
          try {
            const capacitiesResponse = await apiService.getUserCapacities();
            const hasAdminCapacity = capacitiesResponse.capacities.some(capacity => 
              capacity.club_id === course.club_id && capacity.capacities.includes(Constants.capacity_type.admin)
            );
            setIsAdminUser(hasAdminCapacity);
          } catch (error) {
            // console.error('Failed to check admin capacity:', error);
            // USE TOASTS SO THE ERROR ACTUALLY SHOWS UP TO THE USER ????
          }
        }

        for (let s in course.schedule) {
          const session = course.schedule[s];
          
          for (let s in session.instructors) {
            const instructor = session.instructors[s];

            if (!instructors.find(i => i._id === instructor._id)) {
              setInstructors(prev => ([...prev, instructor]));
            }
          }
        }

        setLoading(false);
        setRefreshCourse(false);
      }).catch((error: AxiosError) => {
        navigate("/courses");

        toast.show({
          title: "Course Error",
          content: "Course not found",
          duration: 5000,
          errorDetails: error,
          type: "fail"
        });
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, refreshCourse, cid, instructors, navigate, props.cid, user._id, cameFrom, user.user_type]);

  useEffect(() => {
    if (!course?._id || user.user_type !== Constants.user_type.owner || !helperService.isLoggedIn()) {
      setRegisteredDogs([]);
      return;
    }

    let cancelled = false;
    apiService.getRegisteredCourseDogsByCurrentUser(course._id).then((dogs) => {
      if (!cancelled) {
        setRegisteredDogs(dogs || []);
      }
    }).catch(() => {
      if (!cancelled) {
        setRegisteredDogs([]);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [course?._id, user.user_type, user._id]);

  // Refresh course data when navigating back from registration
  useEffect(() => {
    const queryParameters = new URLSearchParams(window.location.search);
    const redirectStatus = queryParameters.get("redirect_status");
    
    // If redirected back from Stripe payment, refresh course data
    if (redirectStatus === "succeeded" && !loading && !refreshCourse) {
      setRefreshCourse(true);
    }
    
    if (location.state?.refreshCourse && !loading && !refreshCourse) {
      // Clear the state
      window.history.replaceState({}, document.title, location.pathname);
      // Trigger refresh
      setRefreshCourse(true);
    }
  }, [location.state, location.search, loading, refreshCourse, location.pathname]);

  return (
    <div className={`${styles.Course} ${props.cid || cid ? styles.modaled : ""}`}>
      <Helmet>
        {course?.title && <title>{course?.title} Course - {course?.club_name ?? course?.club.club_name}</title>}
      </Helmet>
        <div className={styles.inner}>
          {!props.cid && <Breadcrumb
              crumbs={crumbs}
          />}
          <div className={styles.content}>
            {isAdmin() && course?.status === Constants.course_status.draft && (
              <div className={styles.draftBanner}>
                <div className={styles.draftBannerContent}>
                  <div className={styles.draftBannerText}>
                    This course is currently not visible to competitors. Be sure to publish it when you're ready.
                  </div>
                </div>
                <Button className={styles.draftBannerButton} onClick={() => { setModalContent("publishCourse"); toggle(); }}>Publish Now</Button>
              </div>
            )}
            {course && <div className={styles.admin}>
              <div className={styles.courseStatus}>
                <div
                  className={`${styles.status} ${styles[course.status || ""]} ${isAdmin() ? styles.clickable : ""}`}
                  onClick={isAdmin() ? () => {
                    setCourseFormInitialTab(COURSE_VISIBILITY_STEP);
                    setModalContent("editCourse");
                    toggle();
                  } : undefined}
                >{helperService.getEventStatus(course.status)}</div>
                {publishTimeLabel() && <div className={styles.publishedOn}>{publishTimeLabel()}</div>}
              </div>
              {isAdmin() && <div className={styles.courseActions}>
                <div className={styles.adminActionWrap}><button onClick={() => { setCourseFormInitialTab(undefined); toggle(); setModalContent("editCourse"); }} className={styles.edit}>Edit Course</button></div>
                <div className={styles.adminActionWrap}><Link to={`/c/${course?.handle || props.cid || cid}/manage`} className={styles.manage} title="Manage Course">Manage Course</Link></div>
                <div className={styles.adminActionWrap}><button onClick={() => { toggle(); setModalContent("archiveCourse"); }} className={styles.delete}>Delete Course</button></div>
              </div>}
            </div>}
            <div className={styles.contentInner}>
              <div className={styles.left}>
                <div className={styles.club}>
                  <div className={styles.clubLogo}>
                    <img src={course?.club.logo_url} alt={course?.club.club_name} />
                  </div>
                  <div className={styles.clubName}>
                    <span className={styles.name}>{course?.club.club_name}</span>
                    <span className={styles.number}><Link to={"/k/" + course?.club_id}>Visit Club Page</Link></span>
                  </div>
                </div>
                <div className={styles.titleWrap}>
                  <h1>{course?.title}</h1>
                  {course && (
                    <div className={styles.courseType}>
                      {course.course_type === Constants.course_type.seminar ? "Seminar" : 
                       course.course_type === Constants.course_type.drop_in ? "Class" : "Course"}
                    </div>
                  )}
                </div>
                <div className={styles.sessionCount}>{course?.schedule.length} {course?.schedule.length === 1 ? "Session" : "Sessions"}</div>
                {registeredDogs.length > 0 && <div className={styles.registeredDogsRow}>
                  <ul>
                    {registeredDogs.map((dog) => (
                      <li key={dog._id}><DogPortrait className="smallDogPortrait" showName={false} dog={dog} /></li>
                    ))}
                    <li className={styles.tag}>Registered</li>
                  </ul>
                </div>}
                <div className={styles.description} dangerouslySetInnerHTML={{ __html: course?.description || "" }}></div>
                <div className={styles.details}>
                  <div className={styles.row}>
                    <div className={styles.col}>
                      <div className={styles.label}><span className={`${styles.icon} ${styles.startdate}`}></span>Start Date</div>
                      {course?.start_date && <div className={styles.value}>{moment(course?.start_date).format("MMM Do, YYYY")}</div>}
                    </div>
                    <div className={styles.col}>
                      <div className={styles.label}><span className={`${styles.icon} ${styles.hourglass}`}></span>Time</div>
                      {course?.schedule[0] && <div className={styles.value}>{moment(course?.schedule[0]?.start_date).format("h:mm A")} - {moment(course?.schedule[0]?.end_date).format("h:mm A")}</div>}
                    </div>
                    <div className={styles.col}>
                      <div className={styles.label}><span className={`${styles.icon} ${styles.pin}`}></span>Location</div>
                      <div className={styles.value}>
                        <div className={styles.address}>
                          {course?.address && course?.address?.line1 && <span>{course.address.line1} {course?.address.line2 && course?.address.line2.length > 0 && <span>{course.address.line2}</span>}</span>}
                          {course?.address && course?.address?.city && course?.address.city.length > 0 && <span>{course.address.city}</span>}
                          {course?.address && course?.address?.state && course?.address?.country && course.address.state.length > 0 && course.address.country.length > 0 && <span>{course.address.state}, {course.address.country}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={`${styles.row} ${styles.lastChild}`}>
                    <div className={styles.col}>
                      <div className={styles.label}><span className={`${styles.icon} ${styles.startdate}`}></span>End Date</div>
                      {course?.end_date && <div className={styles.value}>{moment(course?.end_date).format("MMM Do, YYYY")}</div>}
                    </div>
                    <div className={styles.col}>
                      <div className={styles.label}><span className={`${styles.icon} ${styles.time}`}></span>Duration</div>
                      <div className={styles.value}>{course?.schedule.length || 0} Sessions</div>
                    </div>
                    <div className={styles.col}>
                      <div className={styles.label}><span className={`${styles.icon} ${styles.person}`}></span>Instructors</div>
                      <div className={styles.value}>
                        {Array.from(new Map(instructors.map(i => {
                            const key = `${(i.first_name || "").trim().toLowerCase()}|${(i.last_name || "").trim().toLowerCase()}`;
                            return [key, i];
                          })).values()).map((instructor: any, index: number) => (
                          <div key={instructor._id + index} className={styles.instructor}>
                            <span className={styles.name}>{instructor.first_name} {instructor.last_name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
            </div>
            <div className={styles.right}>
              <div className={styles.pricePanel}>
                  <div className={styles.price}>
                      <span className={styles.dollarSign}>$</span>
                      {course?.fee && course?.currency && (
                        <span className={styles.feeWrap}>
                          <span className={styles.fee}>{getCourseFee()}</span>
                          <span className={styles.currency}>{course.currency!.toUpperCase()}</span>
                        </span>
                      )}
                  </div>
                  {course && <div className={styles.pricingLabel}>{getPricingLabel()}</div>}
                  {!isAdmin() && <div className={styles.registerWrap}>
                    {isCourseFull() ? (
                      <span className={`${styles.register} ${styles.registerDisabled}`} aria-disabled="true">{isSeminar ? "Seminar Full" : "Course Full"}</span>
                    ) : waitlistOpen && !isClass ? (
                      <Link className={styles.register} to={"/c/" + cid + "/register"}>Join Waitlist</Link>
                    ) : isCourseLive() ? (
                      <Link className={styles.register} to={"/c/" + cid + "/register"}>{registeredDogs.length > 0 ? "Register Again" : "Register"} {!!course?.fee &&<span className={styles.feeMobile}>(${course!.fee})</span>}</Link>
                    ) : (
                      <>
                        <span className={`${styles.register} ${styles.registerDisabled}`} aria-disabled="true">Register</span>
                        <div className={styles.registerHint}>This course isn't live yet.</div>
                      </>
                    )}
                  </div>}
                  {isAdmin() && <div className={styles.registerWrap}>
                    <Button onClick={() => { setModalContent("searchUsersModal"); toggle(); }} className={styles.registerButton}>Add Manual Entry {!!course?.fee &&<span className={styles.feeMobile}>(${course!.fee})</span>}</Button>
                  </div>}
                  <div className={styles.statBox}>
                    <div className={styles.metric}><span>{enrolledCount}</span>/{entryLimit}</div>
                    <div className={styles.label}>{isSeminar ? "People" : "Dogs"}</div>
                  </div>
                  {waitlistSize > 0 && <div className={styles.statBox}>
                    <div className={styles.metric}><span>{waitlistCount}</span>/{waitlistSize}</div>
                    <div className={styles.label}>{isSeminar ? "People Waitlisted" : "Dogs Waitlisted"}</div>
                  </div>}
              </div>
            </div>
            </div>
        </div>
        <div className={styles.tables}>
          <TabContent className={styles.courseTabs} setTab={setTab} activeTab={activeTab} defaultTab={0} tabs={tabs}>
            <CourseSchedule
              course={course}
              onFilterDogsBySession={(session: number) => {
                const handle = course?.handle || props.cid || cid;
                if (handle) {
                  navigate(`/c/${handle}/manage?session=${session}`);
                }
              }}
              isAdmin={isAdmin()}
              onAddSession={isAdmin() ? () => setShowAddSession(true) : undefined}
            />
            {course ? <CourseDocumentsTab course={course} isAdmin={isAdmin()} /> : <div />}
          </TabContent>
        </div>
      </div>
      <Modal
        className={modalContent === "editCourse" ? "createCourse" : (modalContent || "")}
        isShown={isShown}
        hide={() => {
          toggle();
          setModalContent(null);
          setCourseFormInitialTab(undefined);
        }}
        modalContent={getModalContent()}
      />
      {showAddSession && course && (
        <AddSessionModal
          isShown={showAddSession}
          course={course}
          onClose={() => setShowAddSession(false)}
          onSuccess={() => setRefreshCourse(true)}
        />
      )}
    </div>
  );
};

export default Course;
